import { toNumber } from '@albacross/frontend-utils'

export const BANNER_TYPES = { image: 'image', html5: 'html5' } as const

export type BannerType = (typeof BANNER_TYPES)[keyof typeof BANNER_TYPES]

export type CampaignBanner = {
  id: number
  type: string
  title: string
  width: number
  height: number
  filename: string
  urlAppend: string
  impressionPixel: string
  clickPixel: string
  unsupported: boolean
  url: { download: string; update: string }
}

export const emptyBanner = (): CampaignBanner => ({
  id: 0,
  type: '',
  title: '',
  width: 0,
  height: 0,
  filename: '',
  urlAppend: '',
  impressionPixel: '',
  clickPixel: '',
  unsupported: false,
  url: { download: '', update: '' },
})

const urlFromServer = (x?: any) => (x ? { download: x.get, update: x.put } : { download: '', update: '' })

export const bannersFromServer = (x?: any): CampaignBanner =>
  x
    ? {
        id: x.id,
        type: x.type,
        title: x.title,
        width: x.width,
        height: x.height,
        filename: x.filename,
        urlAppend: x.url_append,
        impressionPixel: x.tracking_pixel_impressions,
        clickPixel: x.tracking_pixel_clicks,
        unsupported: x.unsupported,
        url: urlFromServer(x.url),
      }
    : emptyBanner()

export const bannerToServer = (payload, file, uuid?: any) => ({
  uuid: uuid || '',
  title: payload.title,
  width: toNumber(payload.width),
  height: toNumber(payload.height),
  filename: file?.name || '',
  ext: file?.name.split('.').pop() || '',
})

// Mirrors the customer-facing uploader's 2 MB limit.
export const MAX_BANNER_SIZE_BYTES = 2_000_000

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif']

// Returns a human-readable reason the file is invalid, or null when it passes.
export const validateBannerFile = (file, type: BannerType) => {
  if (file.size > MAX_BANNER_SIZE_BYTES) {
    const sizeMb = (file.size / 1_000_000).toFixed(1)
    const limitMb = MAX_BANNER_SIZE_BYTES / 1_000_000
    return `is too large (${sizeMb} MB, max ${limitMb} MB)`
  }

  if (type === BANNER_TYPES.image && !ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'must be a PNG, JPEG, or GIF image'
  }

  if (type === BANNER_TYPES.html5 && !file.name.toLowerCase().endsWith('.zip')) {
    return 'must be a .zip archive'
  }

  return null
}
